// src/components/admin/Verification.jsx
import * as React from 'react';
import {
  Paper, Typography, Stack, Alert, CircularProgress, TextField, Grid,
  Button, Chip, InputAdornment, Box, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton,
  Checkbox, ListItemIcon, MenuItem
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import verificationI18n from '../../i18n/components/admin/verification.i18n';
import pricelistI18n from '../../i18n/components/tarifs/pricelist.i18n';

import { usePricingData } from '../../context/PricingDataContext';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';
const BLUE_BG = 'rgba(11,95,255,0.08)';
const BORDER = '#e6efe8';
const CELL_HEIGHT = 40;
const money = (n) => Number(n || 0).toFixed(2);
const round2 = (n) => Math.round((Number(n || 0)) * 100) / 100;
const REPORT_COUNTRIES = [
  { code: 'FR', label: 'France', language: 'fr', languageLabel: 'Français' },
  { code: 'BE', label: 'Belgique', language: 'fr', languageLabel: 'Français' },
  { code: 'CH', label: 'Suisse', language: 'fr', languageLabel: 'Français' },
  { code: 'GB', label: 'Royaume-Uni', language: 'en', languageLabel: 'English' },
  { code: 'IE', label: 'Irlande', language: 'en', languageLabel: 'English' },
  { code: 'PL', label: 'Pologne', language: 'pl', languageLabel: 'Polski' },
  { code: 'ES', label: 'Espagne', language: 'es', languageLabel: 'Español' },
  { code: 'IT', label: 'Italie', language: 'it', languageLabel: 'Italiano' },
  { code: 'DE', label: 'Allemagne', language: 'de', languageLabel: 'Deutsch' },
  { code: 'AT', label: 'Autriche', language: 'de', languageLabel: 'Deutsch' },
  { code: 'PT', label: 'Portugal', language: 'pt', languageLabel: 'Português' },
  { code: 'CZ', label: 'République tchèque', language: 'cs', languageLabel: 'Čeština' },
  { code: 'NL', label: 'Pays-Bas', language: 'nl', languageLabel: 'Nederlands' },
];
const languageForReportCountry = (country) =>
  REPORT_COUNTRIES.find((item) => item.code === country)?.language || 'fr';

// 🟣 Normalisation libellé : "Envoie ! on trie pour toi !" (FR) même si un ancien texte EN/PL est stocké en base
const isBatchSortLabel = (raw) => {
  const s = String(raw || '').toLowerCase();
  return (
    s.includes('vendez en 1 clic') ||
    s.includes('wyślij, a my posortujemy') ||
    s.includes('wyslij, a my posortujemy')
  );
};

const displayProductLabel = (raw, t) => {
  if (isBatchSortLabel(raw)) {
    return t('tarifs.pricelist.batchSort.title', 'Envoie ! on trie pour toi !');
  }
  return String(raw || '').trim();
};

function clampInt(v, min = 0, max = Number.MAX_SAFE_INTEGER) {
  const n = parseInt(String(v ?? '').replace(/[^\d-]/g, ''), 10);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}
const isAllowedGrade = (g) => {
  const v = String(g || '').toUpperCase();
  return ['A', 'B', 'C', 'D', 'X', 'HS', 'NF'].includes(v);
};

export default function Verification() {
  const { id } = useParams();
  const nav = useNavigate();
  const { state } = useLocation();
  const readonly = !!state?.readonly;

  const { t, register } = useI18n();
  React.useEffect(() => {
    register('adminVerification', verificationI18n);
    register('tarifs', pricelistI18n); // pour tarifs.pricelist.* (batchSort.title)
  }, [register]);

  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  const [tx, setTx] = React.useState(null);

  // Affichages globaux
  const [qtyReceivedStr, setQtyReceivedStr] = React.useState('');
  const [qtyVerifiedStr, setQtyVerifiedStr] = React.useState(''); // affichage (dérivé)
  const [marginPercentStr, setMarginPercentStr] = React.useState('');
  const [reportMode, setReportMode] = React.useState('detail');
  const [reportCountry, setReportCountry] = React.useState('FR');
  const [buybackOfferAmount, setBuybackOfferAmount] = React.useState('');
  const [pdfOpen, setPdfOpen] = React.useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = React.useState('');
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [pdfErr, setPdfErr] = React.useState('');
  const [addModelOpen, setAddModelOpen] = React.useState(false);
  const [addModelQuery, setAddModelQuery] = React.useState('');
  const [selectedModels, setSelectedModels] = React.useState([]); // [{ id, label, price }]
  const canEdit = !readonly;
  const [searchTerm, setSearchTerm] = React.useState('');

  // Pricing data (pour le dialog "Ajouter un modèle")
  const {
    models,
    loading: pricingLoading,
    error: pricingError,
  } = usePricingData();

  /**
   * groups = [
   *   {
   *     itemId, label, qtySent,
   *     allocs: [
   *       { id, grade, qty, unit }  // lignes de répartition manuelles
   *     ]
   *   }
   * ]
   */
  const [groups, setGroups] = React.useState([]);

  const sumQty = (items = []) => items.reduce((s, it) => s + (Number(it.qty) || 0), 0);

  // 🧠 Normalisation : on privilégie, dans l'ordre :
  // 1) it.allocs (renvoyé par /verify)
  // 2) reconstruction depuis buyQty/buyUnitPrice/grade (dernier contrôle persisté)
  // 3) défaut : toute la quantité envoyée sur une seule ligne Grade A
  function normaliseTx(data) {
    const groups = (data.items || []).map((it) => {
      const qtySent = Math.max(0, Number(it.qty) || 0);
      const defaultGrade = isAllowedGrade(it.grade) ? String(it.grade).toUpperCase() : 'A';
      const defaultUnit  = Number(it.buyUnitPrice ?? it.unitPrice ?? 0);
      const baseUnit     = Number(it.unitPrice) || defaultUnit || 0;

      let allocs = [];

      // (1) cas: le back renvoie déjà des allocations détaillées
      if (Array.isArray(it.allocs) && it.allocs.length) {
        allocs = it.allocs.map((s, i) => {
          const unitVal = Math.max(0, Number(s.unit ?? defaultUnit ?? 0));
          const testerVal = Math.max(0, Number(s.testerUnit ?? 0));
          return {
            id: `${it.id}::${i + 1}`,
            grade: String(s.grade || defaultGrade).toUpperCase(),
            qty: clampInt(s.qty, 0),
            unit: unitVal,          // Prix rachat (avec marge)
            testerUnit: testerVal,  // Prix testeur (interne)
            baseUnit,               // Prix de base indicatif
          };
        });
      } else if (Number(it.buyQty || 0) > 0 || String(it.grade || '').toUpperCase() === 'X') {
        // (2) reconstruire depuis le dernier contrôle: OK + X pour le reste
        const okQty = Math.max(0, Number(it.buyQty) || 0);
        const okGrade = isAllowedGrade(it.grade) ? String(it.grade).toUpperCase() : 'A';
        const okUnit = Math.max(0, Number(it.buyUnitPrice ?? it.unitPrice ?? 0));

        if (okQty > 0) {
          allocs.push({
            id: `${it.id}::1`,
            grade: okGrade === 'X' ? 'A' : okGrade,
            qty: clampInt(okQty, 0),
            unit: okUnit,            // Prix rachat courant
            testerUnit: 0,           // Prix testeur par défaut à 0
            baseUnit,
          });
        }

        const koQty = Math.max(0, qtySent - okQty);
        if (koQty > 0) {
          allocs.push({
            id: `${it.id}::2`,
            grade: 'X',
            qty: koQty,
            unit: 0,
            testerUnit: 0,
            baseUnit,
          });
        }
      } else {
        // (3) défaut : tout en 1 ligne
        // 👉 Par défaut (aucune vérif sauvegardée) : tout à 0 dans le tableau
        //    - Prix test = 0
        //    - Qté = 0
        //    - Prix rachat = 0
        // La quantité envoyée par le client reste stockée dans `qtySent`
        // et s'affiche dans le petit récap de la ligne.
        allocs = [{
          id: `${it.id}::1`,
          grade: defaultGrade,
          qty: 0,
          unit: 0,
          testerUnit: 0,
          baseUnit,
        }];
      }

      return { itemId: it.id, label: it.label, qtySent, allocs };
    });

    return { ...data, _groups: groups };
  }

  const buildModelLabel = (m) => {
    if (!m) return '';
    const brandName = m?.brand?.brand || '';
    const rawName = String(m.model || '');
    const cap = m.capacityGb ? ` (${m.capacityGb} Go)` : '';
    return `${brandName} ${rawName}${cap}`.trim();
  };

  // Chargement
  const load = React.useCallback(async () => {
    setErr(''); setOk(''); setLoading(true);
    try {
      const { data } = await api.get(`/transactions/${id}`);
      const norm = normaliseTx(data);

      const defReceived = sumQty(data.items || []);
      const okUnits = (norm._groups || []).reduce(
        (s,g) => s + g.allocs.filter(a => String(a.grade).toUpperCase() !== 'X').reduce((x,a)=> x + a.qty, 0),
        0
      );

      setTx(norm);
      setGroups(norm._groups || []);
      setReportMode(data.reportMode === 'global' ? 'global' : 'detail');
      setReportCountry(REPORT_COUNTRIES.some((item) => item.code === data.reportCountry) ? data.reportCountry : 'FR');
      setBuybackOfferAmount(
        data.buybackOfferAmount !== null && data.buybackOfferAmount !== undefined
          ? String(data.buybackOfferAmount)
          : ''
      );
      setQtyReceivedStr(String(data.quantity_received || defReceived));
      setQtyVerifiedStr(String(data.quantity_verified || okUnits));
    } catch (e) {
      setErr(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Handlers (quantité / grade / prix)
  const setAllocQty = (itemId, allocId, val) => {
    if (!canEdit) return;
    const newQty = clampInt(val, 0);

    setGroups((prev) =>
      prev.map((g) => {
        if (g.itemId !== itemId) return g;
        return {
          ...g,
          allocs: g.allocs.map((a) =>
            a.id === allocId ? { ...a, qty: newQty } : a
          ),
        };
      })
    );
  };

  const setAllocGrade = (itemId, allocId, val) => {
    if (!canEdit) return;
    const gradeUp = String(val || '').toUpperCase();

    setGroups((prev) =>
      prev.map((g) => {
        if (g.itemId !== itemId) return g;
        return {
          ...g,
          allocs: g.allocs.map((a) => {
            if (a.id !== allocId) return a;
            // si HS ou NF tapé manuellement => rachat = 0
            const isSpecial = gradeUp === 'HS' || gradeUp === 'NF';
            return {
              ...a,
              grade: gradeUp,
              unit: isSpecial ? 0 : a.unit,
            };
          }),
        };
      })
    );
  };

  const toggleGradeHS = (itemId, allocId) => {
    if (!canEdit) return;
    setGroups((prev) =>
      prev.map((g) => {
        if (g.itemId !== itemId) return g;
        return {
          ...g,
          allocs: g.allocs.map((a) => {
            if (a.id !== allocId) return a;
            const current = String(a.grade || '').toUpperCase();
            const nextGrade = current === 'HS' ? '' : 'HS';
            // si HS => rachat = 0
            const nextUnit = nextGrade === 'HS' ? 0 : a.unit;
            return { ...a, grade: nextGrade, unit: nextUnit };
          }),
        };
      })
    );
  };

  const toggleGradeNF = (itemId, allocId) => {
    if (!canEdit) return;
    setGroups((prev) =>
      prev.map((g) => {
        if (g.itemId !== itemId) return g;
        return {
          ...g,
          allocs: g.allocs.map((a) => {
            if (a.id !== allocId) return a;
            const current = String(a.grade || '').toUpperCase();
            const nextGrade = current === 'NF' ? '' : 'NF';
            // si NF => rachat = 0
            const nextUnit = nextGrade === 'NF' ? 0 : a.unit;
            return { ...a, grade: nextGrade, unit: nextUnit };
          }),
        };
      })
    );
  };
  const addGroupFromModel = (model) => {
    if (!canEdit || !model) return;

    // Supporte soit l'objet complet (PricingDataContext), soit un objet simplifié { id, label, price }
    const label = model.label ? String(model.label) : buildModelLabel(model);
    const baseUnit = Number(model.price) || 0;

    const modelId = model.id;
    if (!modelId) return;

    setGroups((prev) => [
      ...prev,
      {
        itemId: `virtual:model:${modelId}:${Date.now()}`,
        label,
        // Pas de quantité déclarée par le client pour ces lignes ajoutées
        qtySent: 0,
        allocs: [
          {
            id: `virtual:${modelId}:1`,
            grade: 'A',
            // quantité initiale à 0
            qty: 0,
            // 👉 par défaut, tout à 0 tant que rien n'est saisi
            testerUnit: 0,
            unit: 0,
            baseUnit,
          },
        ],
      },
    ]);
  };

  const addAllocBelow = (itemId, afterId) => {
    if (!canEdit) return;
    setGroups((prev) =>
      prev.map((g) => {
        if (g.itemId !== itemId) return g;
        const idx = g.allocs.findIndex((a) => a.id === afterId);
        if (idx === -1) return g;

        const base = g.allocs[idx];
        const newAlloc = {
          id: `${g.itemId}::${g.allocs.length + 1}`,
          grade: base.grade || 'A',
          qty: 1,
          unit: base.unit || 0,
          testerUnit: base.testerUnit ?? base.unit ?? 0,
          baseUnit: base.baseUnit ?? 0,
        };

        const newAllocs = [
          ...g.allocs.slice(0, idx + 1),
          newAlloc,
          ...g.allocs.slice(idx + 1),
        ];

        return { ...g, allocs: newAllocs };
      })
    );
  };
  const setTesterUnit = (itemId, allocId, val) => {
    if (!canEdit) return;
    let n = Number(String(val || '').replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) n = 0;
    const testerUnit = round2(n);
    setGroups(prev =>
      prev.map(g => {
        if (g.itemId !== itemId) return g;
        return {
          ...g,
          allocs: g.allocs.map(a =>
            a.id === allocId ? { ...a, testerUnit } : a
          ),
        };
      })
    );
  };

  const removeAlloc = (itemId, allocId) => {
    if (!canEdit) return;

    setGroups((prev) =>
      prev
        .map((g) => {
          if (g.itemId !== itemId) return g;

          const isVirtualGroup = String(g.itemId).startsWith('virtual:');

          // 1) Group "virtual" (ajouté via "Ajouter un modèle")
          //    - Si dernière ligne => on supprime le groupe entier
          //    - Sinon => on supprime juste la ligne
          if (isVirtualGroup) {
            if (g.allocs.length <= 1) return null;
            return { ...g, allocs: g.allocs.filter((a) => a.id !== allocId) };
          }

          // 2) Group "réel" (item en base)
          //    Objectif UX: permettre de retirer un produit de l'écran.
          //    On ne supprime pas l'item côté API (id requis), mais on le masque dans l'UI
          //    et on remet ses quantités à 0 pour ne pas impacter les totaux.

          // Si on enlève la dernière ligne:
          // - si c'est une ligne ajoutée au contrôle (qtySent === 0) => on supprime le groupe entier
          //   pour que le payload n'envoie plus cet item et que le back le supprime (TransactionItem qty=0).
          // - si c'est une ligne déclarée par le client (qtySent > 0) => on ne supprime pas côté API,
          //   on la masque seulement dans l'UI et on remet la qty à 0.
          if (g.allocs.length <= 1) {
            if (g.allocs[0].id !== allocId) return g;

            const isExtraItem = Number(g.qtySent || 0) === 0;
            if (isExtraItem) {
              return null;
            }

            return {
              ...g,
              // Marque l'item DB comme supprimé pour le back
              _deleted: true,
              // Masque côté UI
              _hidden: true,
              // On neutralise aussi la seule ligne côté UI
              allocs: [{ ...g.allocs[0], qty: 0, grade: 'X', unit: 0, testerUnit: 0 }],
            };
          }

          // Sinon: on "supprime" la ligne côté UI, mais on la conserve côté payload
          // pour que le back puisse bien écraser l'ancienne allocation.
          // Comme les splits n'ont pas d'id côté API, si on retire une ligne du tableau,
          // le back peut conserver l'ancienne ligne. On marque donc la ligne comme supprimée
          // et on la neutralise (qty=0, grade=X, prix=0).
          const nextAllocs = g.allocs.map((a) => {
            if (a.id !== allocId) return a;
            return {
              ...a,
              _deleted: true,
              grade: 'X',
              qty: 0,
              unit: 0,
              testerUnit: 0,
            };
          });

          return { ...g, allocs: nextAllocs };
        })
        .filter(Boolean)
    );
  };

  const setAllocUnit = (itemId, allocId, val) => {
    if (!canEdit) return;
    let n = Number(String(val || '').replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) n = 0;
    const unit = round2(n);
    setGroups(prev => prev.map(g => {
      if (g.itemId !== itemId) return g;
      return {
        ...g,
        allocs: g.allocs.map(a => a.id === allocId ? { ...a, unit } : a)
      };
    }));
  };

  // Compteurs live
  const itemsOkLive = groups.reduce((s, g) => s + g.allocs
    .filter(a => {
      const gup = String(a.grade).toUpperCase();
      return gup !== 'X' && gup !== 'HS' && gup !== 'NF';
    })
    .reduce((x, a) => x + a.qty, 0), 0);

  // Pièces envoyées : quantité déclarée par le client (non modifiable)
  const totalSent = groups.reduce(
    (s, g) => s + (Number(g.qtySent) || 0),
    0
  );

  // Pièces reçues : somme des quantités réellement saisies dans le tableau
  const itemsReceivedFromGroups = groups.reduce(
    (s, g) => s + g.allocs.reduce((x, a) => x + (Number(a.qty) || 0), 0),
    0
  );

  const itemsReceivedLive = itemsReceivedFromGroups;
  const itemsKoLive = Math.max(0, itemsReceivedLive - itemsOkLive);

  const totalBuyback = groups.reduce((s, g) => s + g.allocs
    .filter(a => {
      const gup = String(a.grade).toUpperCase();
      return gup !== 'X' && gup !== 'HS' && gup !== 'NF';
    })
    .reduce((x, a) => x + a.qty * (Number(a.unit) || 0), 0), 0);

  const totalTester = groups.reduce(
    (s, g) =>
      s +
      g.allocs
        .filter((a) => {
          const gup = String(a.grade).toUpperCase();
          return gup !== 'X' && gup !== 'HS' && gup !== 'NF';
        })
        .reduce(
          (x, a) => x + a.qty * (Number(a.testerUnit) || 0),
          0
        ),
    0
  );
  // 🔹 Sauvegarde du total testeur dans le navigateur pour cette transaction
  React.useEffect(() => {
    if (!id) return;
    try {
      localStorage.setItem(`tx_sri_${id}`, String(totalTester || 0));
    } catch (e) {
      // ignore si localStorage indispo
    }
  }, [id, totalTester]);
  
  const totalMargin = totalTester - totalBuyback;

  
  const buildVerificationPayload = () => {
    // On envoie TOUS les allocs, même si qty = 0
    const itemsForApi = groups
      // Ne pas envoyer les groupes supprimés/masqués quand ils sont des "extra" (qtySent === 0)
      .filter((g) => !(g?._hidden && Number(g?.qtySent || 0) === 0))
      .map((g) => {
        const isVirtual = String(g.itemId).startsWith('virtual:model:');

        // Si un item DB est marqué supprimé côté UI, on demande au back de le supprimer.
        // (Le back supporte déjà `it._deleted === true`.)
        if (!isVirtual && g?._deleted) {
          return { id: g.itemId, _deleted: true };
        }

        const splits = g.allocs.map((a) => {
          const gradeRaw = String(a.grade || '').toUpperCase();
          return {
            qty: clampInt(a.qty, 0),
            grade: gradeRaw,                 // A/B/C/D/HS/NF/X...
            buyUnitPrice: round2(a.unit),    // prix de rachat saisi
            testerUnit: round2(a.testerUnit) // prix testeur
          };
        });

        if (isVirtual) {
          // itemId = "virtual:model:<modelId>:<timestamp>"
          const parts = String(g.itemId).split(':');
          const modelId = parts[2] || null;

          return {
            modelId,
            label: g.label,
            splits,
          };
        }

        // Item existant lié à un TransactionItem en base
        return {
          id: g.itemId,
          splits,
        };
      })
      .filter(Boolean); // ok de garder, on ne retourne plus null mais c'est safe

    const received = itemsReceivedFromGroups;
    const verified = itemsOkLive;

    return {
      quantity_received: received,
      quantity_verified: verified,
      itemsReceived: received,
      itemsOk: verified,
      comment: tx?.comment || '',
      items: itemsForApi,
      reportMode,
      reportCountry,
      reportLanguage: languageForReportCountry(reportCountry),
      buybackOfferAmount: String(buybackOfferAmount).trim() === ''
        ? null
        : round2(String(buybackOfferAmount).replace(',', '.')),
    };
  };
  

  const submitVerification = async () => {
    setErr('');
    setOk('');
    try {
      // On force le statut "reçu" au cas où
      try {
        await api.patch(`/transactions/${id}`, { statusReception: true });
      } catch (_) {}

      const basePayload = buildVerificationPayload();
      const payload = { ...basePayload, draft: false };

      const { data } = await api.post(`/transactions/${id}/verify`, payload);

      const norm = normaliseTx(data);
      setTx(norm);
      setGroups(norm._groups || []);
      setQtyReceivedStr(String(data.quantity_received || basePayload.quantity_received));
      setQtyVerifiedStr(String(data.quantity_verified || basePayload.quantity_verified));
      setOk(t('adminVerification.success.saved') || 'Enregistré');
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
          (t('adminVerification.errors.verify') || 'Erreur lors de la vérification')
      );
    }
  };
  
  
    const saveDraft = async () => {
    setErr(''); setOk('');
    try {
      // On peut aussi mettre la transaction en "réceptionné" pour suivre le flux
      try { await api.patch(`/transactions/${id}`, { statusReception: true }); } catch (_) {}

      const basePayload = buildVerificationPayload();
      const payload = { ...basePayload, draft: true };

      const { data } = await api.post(`/transactions/${id}/verify`, payload);

      const norm = normaliseTx(data);
      setTx(norm);
      setGroups(norm._groups || []);
      setQtyReceivedStr(String(data.quantity_received || basePayload.quantity_received));
      setQtyVerifiedStr(String(data.quantity_verified || basePayload.quantity_verified));
      setOk('Brouillon sauvegardé');
    } catch (e) {
      setErr(e?.response?.data?.message || 'Erreur lors de la sauvegarde du brouillon');
    }
  };
  

  // --- Add-model dialog helpers (multi-select) ---
  const clearModelSelection = () => setSelectedModels([]);

  const toggleModelSelection = (m) => {
    if (!m) return;
    const label = buildModelLabel(m);
    setSelectedModels((prev) => {
      const exists = prev.some((x) => String(x.id) === String(m.id));
      if (exists) return prev.filter((x) => String(x.id) !== String(m.id));
      return [...prev, { id: m.id, label, price: Number(m.price || 0) }];
    });
  };

  const addSelectedModelsToGroups = () => {
    if (!canEdit || !selectedModels.length) return;

    // Ajout dans l'ordre de sélection
    selectedModels.forEach((sm) => {
      addGroupFromModel({ id: sm.id, label: sm.label, price: sm.price });
    });

    clearModelSelection();
    setAddModelOpen(false);
    setAddModelQuery('');
  };

  // PDF dialog helpers
  const openPdf = async () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl('');
    }
    setPdfErr('');
    setPdfLoading(true);
    setPdfOpen(true);
    try {
      const resp = await api.get(`/transactions/${id}/verification.pdf?ts=${Date.now()}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setPdfErr(e?.response?.data?.message || 'Erreur lors du chargement du PDF.');
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdf = () => {
    setPdfOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
    }
    setPdfBlobUrl('');
    setPdfErr('');
  };

  React.useEffect(() => () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
  }, [pdfBlobUrl]);

  // UI
  if (loading) {
    return (
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18}/>
          {t('adminVerification.loading')}
        </Stack>
      </Paper>
    );
  }

  if (!tx) {
    return (
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        {err ? <Alert severity="error">{err}</Alert> : <Typography>{t('adminVerification.empty')}</Typography>}
      </Paper>
    );
  }


  const applyMarginToAll = () => {
    if (!canEdit) return;
    const raw = String(marginPercentStr || '').replace(',', '.').trim();
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return;

    setGroups((prev) =>
      prev.map((g) => ({
        ...g,
        allocs: g.allocs.map((a) => {
          const gradeUp = String(a.grade || '').toUpperCase();
          const isSpecial = gradeUp === 'HS' || gradeUp === 'NF';

          // HS / NF => rachat toujours à 0
          if (isSpecial) {
            return { ...a, unit: 0 };
          }

          const tester = Number(a.testerUnit || 0);
          const rawUnit = Math.max(0, Math.round(tester * (1 - n / 100)));
          const base = Number(a.baseUnit ?? tester);
          // le prix de rachat ne peut pas dépasser le prix de base
          const newUnit = Math.min(rawUnit, base);
          return { ...a, unit: newUnit };
        }),
      }))
    );
  };

  // --- Search/filter for products ---
  const normalizedSearch = String(searchTerm || '').trim().toLowerCase();
  const visibleGroups = groups.filter((g) => !g?._hidden);
  const filteredGroups = normalizedSearch
    ? visibleGroups.filter((g) =>
        displayProductLabel(g.label || '', t)
          .toLowerCase()
          .includes(normalizedSearch)
      )
    : visibleGroups;

  return (
    <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {t('adminVerification.title')} — {tx.numPackage}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="text" onClick={() => nav('/admin/transactions')}>
            {t('adminVerification.back')}
          </Button>
        </Stack>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}

      {/* En-tête simple */}
      <Stack spacing={1.25} sx={{ mb: 2 }}>
        <Typography sx={{ color: 'text.secondary' }}>
          {t('adminVerification.client')}: {tx?.user?.email || tx?.user?.companyName || '—'}
        </Typography>
      </Stack>

      {/* Résumé live */}
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        <Grid item xs={12} sm="auto">
          <Chip label={`${t('adminVerification.chips.received')} ${itemsReceivedLive}`} sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 700 }} />
        </Grid>
        <Grid item xs={12} sm="auto">
          <Chip label={`${t('adminVerification.chips.ok')} ${itemsOkLive}`} sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 700 }} />
        </Grid>
        <Grid item xs={12} sm="auto">
          <Chip label={`${t('adminVerification.chips.ko')} ${itemsKoLive}`} variant="outlined" sx={{ borderColor: BLUE, color: BLUE, fontWeight: 700 }} />
        </Grid>
        <Grid item xs={12} sm="auto">
          <Chip label={`${t('adminVerification.chips.totalBuyback')} ${money(totalBuyback)} €`} sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 700 }} />
        </Grid>
        <Grid item xs={12} sm="auto">
          <Chip
            label={`Marge totale ${money(totalMargin)} €`}
            sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 700 }}
          />
        </Grid>
      </Grid>

      {/* Commentaire */}
      <TextField
        fullWidth multiline minRows={2}
        label={t('adminVerification.commentLabel')}
        inputProps={{ readOnly: !canEdit }}
        value={tx.comment || ''}
        onChange={(e)=>setTx(txx=>({ ...txx, comment: e.target.value }))}
        sx={{ mb: 2 }}
      />

      {/* Compteurs (global) */}
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        {/* Pièces envoyées (déclaration client) */}
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="text"
            label="Pièces envoyées (client)"
            value={String(totalSent)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {t('adminVerification.counters.unitPcs')}
                </InputAdornment>
              ),
              readOnly: true,
            }}
          />
        </Grid>

        {/* Pièces reçues (contrôle, calculé à partir du tableau) */}
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="text"
            label="Pièces reçues"
            value={String(itemsReceivedFromGroups)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {t('adminVerification.counters.unitPcs')}
                </InputAdornment>
              ),
              readOnly: true,
            }}
          />
        </Grid>

        {/* Marge (%) */}
        <Grid item xs={12} sm={4}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              type="number"
              inputMode="decimal"
              label="Marge (%)"
              value={marginPercentStr}
              onChange={(e) => setMarginPercentStr(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                readOnly: !canEdit,
              }}
            />
            {canEdit && (
              <Button
                variant="outlined"
                onClick={applyMarginToAll}
                sx={{ whiteSpace: 'nowrap', minWidth: 90 }}
              >
                Appliquer
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* ===== Liste simpliste (Produit | Grade | Qté | Prix €) ===== */}
      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, fontSize: 13 }}>
          <Box sx={{ mr: 2 }}>
            Total test : <strong>{money(totalTester)} €</strong>
          </Box>
          <Box sx={{ mr: 2 }}>
            Total rachat : <strong>{money(totalBuyback)} €</strong>
          </Box>
          <Box>
            Marge totale : <strong>{money(totalMargin)} €</strong>
          </Box>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 1 }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            size="small"
            placeholder="Rechercher un produit…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 320 }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            Produits à vérifier
          </Typography>
        </Stack>
        {/* Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 70px 60px 50px 50px 70px 80px 130px 60px 56px',
            columnGap: 0.75,
            borderBottom: `1px solid ${BORDER}`,
            pb: 0.25,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {t('adminVerification.items.colProduct') || 'Produit'}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            Prix test (€)
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {t('adminVerification.items.colGrade') || 'Grade'}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            HS
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            NF
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Qté
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Rachat (€)
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            Marge (€)
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            Base u.(€)
          </Box>
          <Box />
        </Box>

        {groups.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', mt: 1 }}>—</Typography>
        ) : filteredGroups.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', mt: 1 }}>
            Aucun produit ne correspond à votre recherche.
          </Typography>
        ) : (
          filteredGroups.map((g) => {
            const isVirtualGroup = String(g.itemId).startsWith('virtual:');
            // On masque aussi les lignes "supprimées" persistées (qty=0, grade=X, prix=0)
            const visibleAllocs = g.allocs.filter((a) => {
              if (a?._deleted) return false;
              const q = Number(a?.qty || 0);
              const gup = String(a?.grade || '').toUpperCase();
              const unit = Number(a?.unit || 0);
              const tester = Number(a?.testerUnit || 0);
              // Ligne neutralisée => on la cache
              if (q === 0 && gup === 'X' && unit === 0 && tester === 0) return false;
              return true;
            });
            const totalOkQtyForGroup = visibleAllocs
              .filter((a) => {
                const gup = String(a.grade || '').toUpperCase();
                return gup !== 'X' && gup !== 'HS' && gup !== 'NF';
              })
              .reduce((sum, a) => sum + (Number(a.qty) || 0), 0);
            const totalGroupQty = visibleAllocs.reduce((sum, a) => sum + (Number(a.qty) || 0), 0);

            return (
              <React.Fragment key={g.itemId}>
                {g.allocs.filter(a => !a._deleted).map((a, idx) => {
                  const gradeUp = String(a.grade || '').toUpperCase();
                  const isHS = gradeUp === 'HS';
                  const isNF = gradeUp === 'NF';
                  const isHSorNF = isHS || isNF;
                  const isZeroQty = Number(a.qty || 0) === 0;
                  const rowBg = isZeroQty
                    ? 'rgba(255,160,0,0.16)'
                    : isVirtualGroup
                      ? 'rgba(4,102,200,0.06)'
                      : idx % 2
                        ? 'rgba(11,95,255,0.03)'
                        : 'transparent';

                  return (
                    <Box
                      key={a.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 70px 60px 50px 50px 70px 80px 130px 60px 56px',
                        columnGap: 0.75,
                        py: 0.25,
                        background: rowBg,
                        fontSize: 13,
                      }}
                    >
                      {/* Produit + quantités de base / rachat (infos seulement sur la 1ère ligne) */}
                      <Box sx={{ alignSelf: 'center', display: 'flex', flexDirection: 'column' }}>
                      <Box component="span">
                        {displayProductLabel(g.label, t)}
                      </Box>
                        {idx === 0 && (
                          <Box
                            component="span"
                            sx={{ color: 'text.secondary', fontSize: 12, mt: 0.25 }}
                          >
                            {g.qtySent > 0
                              ? `(qté(b): ${g.qtySent} · qté rachat: ${totalOkQtyForGroup} · qté: ${totalGroupQty})`
                              : `(qté rachat: ${totalOkQtyForGroup} · qté: ${totalGroupQty})`}
                          </Box>
                        )}
                      </Box>

                      {/* Prix testeur (modifiable) */}
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ step: '0.01', min: '0', readOnly: !canEdit }}
                        value={String(a.testerUnit ?? a.unit ?? 0)}
                        onChange={(e) => setTesterUnit(g.itemId, a.id, e.target.value)}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: 13,
                            maxWidth: 70,
                            height: CELL_HEIGHT,
                          },
                        }}
                      />

                      {/* Grade (saisie libre) */}
                      <TextField
                        size="small"
                        inputProps={{
                          readOnly: !canEdit || isHSorNF,
                          style: { textTransform: 'uppercase' },
                        }}
                        value={gradeUp}
                        onChange={(e) => setAllocGrade(g.itemId, a.id, e.target.value)}
                        sx={{
                          '& .MuiInputBase-root': {
                            bgcolor: isHSorNF ? '#f5f5f5' : 'inherit',
                            fontSize: 13,
                            maxWidth: 60,
                            height: CELL_HEIGHT,
                          },
                        }}
                      />

                      {/* Bouton HS */}
                      <Button
                        size="small"
                        variant={isHS ? 'contained' : 'outlined'}
                        color={isHS ? 'primary' : 'inherit'}
                        onClick={() => toggleGradeHS(g.itemId, a.id)}
                        disabled={!canEdit || isNF}
                        sx={{ minWidth: 0, px: 0.5, fontSize: 11, height: CELL_HEIGHT }}
                      >
                        HS
                      </Button>

                      {/* Bouton NF */}
                      <Button
                        size="small"
                        variant={isNF ? 'contained' : 'outlined'}
                        color={isNF ? 'primary' : 'inherit'}
                        onClick={() => toggleGradeNF(g.itemId, a.id)}
                        disabled={!canEdit || isHS}
                        sx={{ minWidth: 0, px: 0.5, fontSize: 11, height: CELL_HEIGHT }}
                      >
                        NF
                      </Button>

                      {/* Qté */}
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ step: '1', min: '0', readOnly: !canEdit }}
                        value={String(a.qty)}
                        onChange={(e) => setAllocQty(g.itemId, a.id, e.target.value)}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: 13,
                            height: CELL_HEIGHT,
                          },
                        }}
                      />

                      {/* Prix rachat (avec marge, modifiable) */}
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ step: '0.01', min: '0', readOnly: !canEdit }}
                        value={String(a.unit ?? 0)}
                        onChange={(e) => setAllocUnit(g.itemId, a.id, e.target.value)}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: 13,
                            height: CELL_HEIGHT,
                          },
                        }}
                      />

                      {/* Marge par ligne (indicative, € + %) */}
                      <TextField
                        size="small"
                        type="text"
                        inputProps={{ readOnly: true }}
                        value={(() => {
                          const tester = Number(a.testerUnit || 0);
                          const unit = Number(a.unit || 0);
                          const qty = Number(a.qty) || 0;
                          const perUnitMargin = tester - unit;
                          const lineMargin = perUnitMargin * qty;
                          const pct =
                            tester > 0 ? Math.round((perUnitMargin / tester) * 100) : 0;
                          return `${money(lineMargin)} € (${pct}%)`;
                        })()}
                        sx={{
                          '& .MuiInputBase-root': {
                            bgcolor: '#f5f5f5',
                            fontSize: 13,
                            maxWidth: 130,
                            height: CELL_HEIGHT,
                          },
                        }}
                      />

                      {/* Prix de base indicatif */}
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ readOnly: true }}
                        value={String(a.baseUnit ?? 0)}
                        sx={{
                          '& .MuiInputBase-root': {
                            bgcolor: '#f5f5f5',
                            fontSize: 13,
                            maxWidth: 60,
                            height: CELL_HEIGHT,
                          },
                        }}
                      />

                      {/* Actions (ajouter / supprimer une ligne) */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {canEdit && (
                          <>
                            <Button
                              size="small"
                              onClick={() => addAllocBelow(g.itemId, a.id)}
                              sx={{ minWidth: 0, px: 0.5, height: CELL_HEIGHT }}
                            >
                              <AddIcon fontSize="small" />
                            </Button>
                            <Button
                              size="small"
                              onClick={() => removeAlloc(g.itemId, a.id)}
                              sx={{ minWidth: 0, px: 0.5, height: CELL_HEIGHT }}
                            >
                              <RemoveIcon fontSize="small" />
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                <Divider sx={{ my: 0.5, borderColor: BORDER }} />
              </React.Fragment>
            );
          })
        )}
        {canEdit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAddModelOpen(true);
                setAddModelQuery('');
                clearModelSelection();
              }}
              sx={{ fontSize: 13 }}
            >
              Ajouter un modèle
            </Button>
          </Box>
        )}
      </Paper>

      {/* Dialog ajout de modèle */}
      <Dialog
        open={addModelOpen}
        onClose={() => {
          setAddModelOpen(false);
          setAddModelQuery('');
          clearModelSelection();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Ajouter un modèle à la vérification
        </DialogTitle>
        <DialogContent dividers>
          {pricingLoading && !models?.length ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 1 }}>
              <CircularProgress size={18} />
              <Typography>Chargement des modèles…</Typography>
            </Stack>
          ) : pricingError && !models?.length ? (
            <Alert severity="error">{String(pricingError)}</Alert>
          ) : (
            <>
              {/* Sélection (visible) */}
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>
                  Sélection ({selectedModels.length})
                </Typography>

                {selectedModels.length ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {selectedModels.map((sm) => (
                      <Chip
                        key={sm.id}
                        label={sm.label}
                        onDelete={() =>
                          setSelectedModels((prev) =>
                            prev.filter((x) => String(x.id) !== String(sm.id))
                          )
                        }
                        sx={{ fontSize: 12.5 }}
                      />
                    ))}
                    <Button
                      size="small"
                      variant="text"
                      onClick={clearModelSelection}
                      sx={{ alignSelf: 'center', fontSize: 12.5 }}
                    >
                      Vider
                    </Button>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    Aucun modèle sélectionné.
                  </Typography>
                )}
              </Box>

              {/* Recherche (sous la sélection) */}
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher une référence (marque, modèle…)"
                value={addModelQuery}
                onChange={(e) => setAddModelQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                <List dense>
                  {models
                    ?.filter((m) => m.isActive !== false)
                    .filter((m) => {
                      const q = addModelQuery.trim().toLowerCase();
                      if (!q) return true;
                      const hay = `${m?.brand?.brand || ''} ${m.model}`.toLowerCase();
                      return hay.includes(q);
                    })
                    .sort((a, b) => {
                      const aLabel = buildModelLabel(a).toLowerCase();
                      const bLabel = buildModelLabel(b).toLowerCase();
                      return aLabel.localeCompare(bLabel);
                    })
                    .map((m) => {
                      const label = buildModelLabel(m);
                      const checked = selectedModels.some(
                        (x) => String(x.id) === String(m.id)
                      );

                      return (
                        <ListItemButton
                          key={m.id}
                          selected={checked}
                          onClick={() => toggleModelSelection(m)}
                        >
                          <ListItemIcon sx={{ minWidth: 34 }}>
                            <Checkbox
                              edge="start"
                              checked={checked}
                              tabIndex={-1}
                              disableRipple
                              size="small"
                            />
                          </ListItemIcon>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ width: '100%' }}
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                                {label}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                {m?.brand?.brand || ''}
                              </Typography>
                            </Box>
                            <Box sx={{ whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600 }}>
                              {money(m.price || 0)} €
                            </Box>
                          </Stack>
                        </ListItemButton>
                      );
                    })}

                  {(!models || !models.length) && (
                    <Typography sx={{ p: 1, fontSize: 13, color: 'text.secondary' }}>
                      Aucun modèle disponible.
                    </Typography>
                  )}
                </List>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={addSelectedModelsToGroups}
            disabled={!selectedModels.length}
            sx={{ fontWeight: 700 }}
          >
            Ajouter {selectedModels.length ? `(${selectedModels.length})` : ''}
          </Button>
          <Button
            onClick={() => {
              setAddModelOpen(false);
              setAddModelQuery('');
              clearModelSelection();
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: '#f8fbff' }}>
        <Typography sx={{ fontWeight: 800, mb: 1.5, color: BLUE }}>
          Rapport d’offre de reprise
        </Typography>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Présentation du rapport"
              value={reportMode}
              onChange={(event) => setReportMode(event.target.value)}
              inputProps={{ readOnly: !canEdit }}
              disabled={!canEdit}
            >
              <MenuItem value="detail">Détaillé — produits et prix unitaires</MenuItem>
              <MenuItem value="global">Global — montant total uniquement</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Pays du destinataire"
              value={reportCountry}
              onChange={(event) => setReportCountry(event.target.value)}
              inputProps={{ readOnly: !canEdit }}
              disabled={!canEdit}
            >
              {REPORT_COUNTRIES.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Langue du rapport"
              value={REPORT_COUNTRIES.find((country) => country.code === reportCountry)?.languageLabel || 'Français'}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              required
              size="small"
              label="Montant final de l’offre"
              value={buybackOfferAmount}
              onChange={(event) => setBuybackOfferAmount(event.target.value)}
              disabled={!canEdit}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>€</Typography> }}
              helperText={String(buybackOfferAmount).trim() === '' ? `Calcul actuel : ${money(totalBuyback)} €` : 'Montant affiché et enregistré dans le PDF'}
            />
          </Grid>
        </Grid>
        <Typography sx={{ mt: 1.25, color: 'text.secondary', fontSize: 13 }}>
          Saisis le montant réellement proposé au client. Le PDF affichera clairement « l’offre de reprise s’élève à… » dans la langue correspondant au pays sélectionné.
        </Typography>
      </Paper>

      {/* Actions */}
      {canEdit && (
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={submitVerification}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER } }}
          >
            {t('adminVerification.actions.submit')}
          </Button>

          <Button
            variant="outlined"
            onClick={saveDraft}
            sx={{ color: BLUE, borderColor: BLUE, '&:hover': { borderColor: BLUE_HOVER } }}
          >
            Sauvegarder
          </Button>

          <Button
            variant="text"
            startIcon={<PictureAsPdfIcon />}
            onClick={openPdf}
            disabled={!tx.statusPdfRecap}
            sx={{ color: BLUE, '&:hover': { backgroundColor: BLUE_BG } }}
          >
            {t('adminVerification.actions.openPdf')}
          </Button>
        </Stack>
      )}
      {/* PDF Dialog */}
      <Dialog open={pdfOpen} onClose={closePdf} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>
          {t('adminVerification.actions.openPdf')}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ width: '100%', height: '80vh', bgcolor: '#f6fbf7' }}>
            {pdfLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 3 }}>
                <CircularProgress size={18} />
                <Typography>Chargement du PDF…</Typography>
              </Stack>
            ) : pdfErr ? (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <Alert severity="error">{pdfErr}</Alert>
              </Stack>
            ) : pdfBlobUrl ? (
              <iframe
                title="verification-pdf"
                src={pdfBlobUrl}
                style={{ width: '100%', height: '100%', border: 0 }}
              />
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <Typography>Aucun PDF disponible.</Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {pdfBlobUrl && (
            <Button
              startIcon={<OpenInNewIcon />}
              href={pdfBlobUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: BLUE }}
            >
              Ouvrir dans un nouvel onglet
            </Button>
          )}
          <Button onClick={closePdf}>{t('adminVerification.actions.close') || 'Fermer'}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
